import { useEffect, useState } from 'react';
import { useModal } from '@lasbe/react-modal';
import { ReviewModal } from '@/components/modal/TextModal';
import { apiHoban } from '@/api';
/**
 * 확률 기반 피드백 모달을 관리하는 커스텀 훅
 *
 * @description 사용자에게 확률적으로 피드백 요청 모달을 표시하고,
 * 별점과 의견을 수집하는 기능을 제공합니다.
 *
 * @example
 * ```javascript
 * const feedback = useFeedbackModal();
 *
 * // 30% 확률로 피드백 모달 표시
 * const success = feedback.showModal(30);
 *
 * // 별점 설정
 * feedback.setRating(5);
 *
 * // 의견 설정
 * feedback.setComment('서비스가 좋아요!');
 *
 * // 피드백 제출
 * feedback.submitFeedback();
 * ```
 *
 * @returns {Object} 피드백 모달 관리 객체
 */

interface FeedbackModalHookReturns {
  isOpen: boolean;

  comment: string;
  setComment: (comment: string) => void;
  showModal: (probability: number) => boolean;
  closeModal: () => void;
  submitFeedback: (rate: number, comment: string) => void;
  skipFeedback: () => void;
}

const useFeedbackModal = (): FeedbackModalHookReturns => {
  const [comment, setComment] = useState(''); // 의견 텍스트
  const { openModal, closeModal } = useModal();
  const [isOpen, setIsOpen] = useState(false); // 내부 상태(호환성 유지용)

  //   const handleConfirm = () => {
  //     // 별점/코멘트 입력 후 확인 시 동작 (필요시 구현)
  //     closeModal();
  //     setIsOpen(false);
  //   };

  useEffect(() => {}, [comment]);

  // ReviewModal이 내부적으로 상태를 관리하고, 제출 시 상위로 값을 콜백하는 구조
  const showModal = (probability: number) => {
    const random = Math.random() * 100;
    const storedFeedback = localStorage.getItem('isFeedback');
    if (storedFeedback === 'true') {
      return false;
    }
    if (random <= probability) {
      setIsOpen(true);
      setComment('');

      openModal({
        content: (
          <ReviewModal
            title="서비스는 어떠셨나요?"
            handleConfirm={(rate: number, comment: string) => {
              submitFeedback(rate, comment);
            }}
            handleSkip={skipFeedback}
          />
        ),
      });
      return true;
    }
    return false;
  };

  // rate, comment를 인자로 받아 처리
  const submitFeedback = async (rate: number, comment: string) => {
    const feedbackData = {
      rate: rate,
      comment,
    };
    console.log('피드백 제출:', feedbackData);
    await apiHoban.post('/rate/register', feedbackData).then(() => {
      closeModal();
      setIsOpen(false);
      setComment('');
      localStorage.setItem('isFeedback', 'true');
    });
  };

  const skipFeedback = () => {
    console.log('피드백 스킵됨');
    closeModal();
    setIsOpen(false);
  };

  return {
    isOpen,
    comment,
    setComment,
    showModal,
    closeModal,
    submitFeedback,
    skipFeedback,
  };
};

export default useFeedbackModal;
